import { parseContractEndDate } from '@order-management/dates';
import { formatSalesOrderNumber } from '@order-management/format';
import {
  AdminPermissions,
  hasPermissions,
  SoftwareSeatPermissions,
} from '@order-management/roles';
import {
  LicenseKeysExpandableTableRow,
  LicenseKeysTableRow,
} from '@order-management/types';
import { differenceInDays } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { Icon } from 'react-bulma-components';
import { FaTrash } from 'react-icons/fa6';
import CheckPermissions from '../../shared/check-permissions/check-permissions';
import Conditional from '../../shared/conditional/conditional';
import useSalesOrderPermissions from '../../shared/hooks/sales-order-permissions';
import useUser from '../../shared/hooks/user';
import If from '../../shared/if/if';
import { SoftwareUnassignmentConfirmationModal } from '../../software-assignment/software-unassignment-confirmation-modal copy/software-unassignment-confirmation-modal';
import Link from '../../ui/link/link';
import MaskedField from '../../ui/masked-field/masked-field';
import PaginatedTable, {
  CustomPaginatedTableProps,
} from '../../ui/table/paginated-table/paginated-table';
import {
  Column,
  ExpandableRow,
  OnTableChangeFunction,
} from '../../ui/table/types';
import { Tooltip } from '../../ui/tooltip/tooltip';

const UNASSIGN_TOOLTIP_MESSAGE = 'Unassign license from this device';

const isLicenseKeyExpired = (row: LicenseKeysTableRow) =>
  !!row.contractEndDate &&
  differenceInDays(row.contractEndDate, new Date()) < 0;

export type LicenseKeysTableProps =
  CustomPaginatedTableProps<LicenseKeysTableRow> & {
    onChange: OnTableChangeFunction<
      LicenseKeysTableRow,
      LicenseKeysExpandableTableRow
    >;
    onUnassign: (deviceId: string) => Promise<void>;
  };

type AssignmentData = {
  deviceId: string;
  licenseKey: string;
};

export function LicenseKeysTable({
  dataSource,
  config,
  onChange,
  onUnassign,
  form,
  empty,
}: LicenseKeysTableProps) {
  const { user } = useUser();
  const {
    userIsJustViewOnlySalesOrderManager,
    userHasSalesOrderManagePermissions,
  } = useSalesOrderPermissions();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [selectedAssignmentData, setSelectedAssignmentData] =
    useState<AssignmentData>();

  const isExpandableRow = useCallback((row: LicenseKeysTableRow) => {
    return row.seats.length > 1;
  }, []);

  const isDisabledRow = useCallback((row: LicenseKeysTableRow) => {
    return isLicenseKeyExpired(row);
  }, []);

  const handleUnassignLicenseClick = useCallback(
    (deviceId: string, licenseKey: string) => {
      setSelectedAssignmentData({ deviceId, licenseKey });
      setShowConfirmationModal(true);
    },
    []
  );

  const handleCancelClick = useCallback(() => {
    setShowConfirmationModal(false);
  }, []);

  const handleConfirmUnassignmentClick = useCallback(async () => {
    await onUnassign(selectedAssignmentData?.deviceId ?? '');
    setShowConfirmationModal(false);
  }, [onUnassign, selectedAssignmentData]);

  const columns: Column<LicenseKeysTableRow, LicenseKeysExpandableTableRow>[] =
    useMemo(
      () => [
        {
          key: 'key',
          className: 'is-width-25',
          render: (el) => {
            const userCanAccessAdminTools = hasPermissions(user, [
              AdminPermissions.ADMIN_TOOLS_ACCESS,
            ]);

            const userShouldNotSeeLicenseKey =
              userIsJustViewOnlySalesOrderManager ||
              (!userCanAccessAdminTools &&
                !userHasSalesOrderManagePermissions(el.salesOrder.id));

            return (
              <MaskedField
                value={el.key}
                disabled={userShouldNotSeeLicenseKey}
              />
            );
          },
          title: 'License key',
          sortable: false,
          noWrap: true,
        },
        {
          key: 'softwareSeats.salesOrderLine.salesOrder.number',
          className: 'is-width-10',
          render: (el) => (
            <Link color="primary" to={`/sales-orders/${el.salesOrder.id}`}>
              {formatSalesOrderNumber(el.salesOrder.number)}
            </Link>
          ),
          title: 'Sales order',
          sortable: true,
        },
        {
          key: 'contractEndDate',
          className: 'is-width-10',
          render: (el) => parseContractEndDate(el.contractEndDate),
          title: 'Contract end date',
          sortable: true,
        },
        {
          key: 'seats.total',
          className: 'is-width-10',
          render: (el) => {
            // Exclude expired license keys
            const availableSeats = isLicenseKeyExpired(el)
              ? 0
              : el.seats.filter((seat) => !seat.assignedDevice).length;

            const totalSeats = el.seats.length;

            return `${availableSeats}/${totalSeats}`;
          },
          title: 'Available/total seats',
          sortable: false,
        },
        {
          key: 'assignedDevice.name',
          className: 'is-width-15',
          render: (el) => {
            if (isExpandableRow(el)) {
              return null;
            }
            const assignedDevice = el.seats[0]?.assignedDevice;
            return (
              <Conditional condition={!!assignedDevice}>
                <Conditional.True>
                  <span>{assignedDevice?.serialNumber}</span>
                  <If condition={!!assignedDevice?.name?.length}>
                    <span> ({assignedDevice?.name})</span>
                  </If>
                  <CheckPermissions
                    permissions={
                      SoftwareSeatPermissions.SOFTWARE_SEATS_UNASSIGN
                    }
                  >
                    <CheckPermissions.Render>
                      <Tooltip message={UNASSIGN_TOOLTIP_MESSAGE}>
                        <Icon
                          color="danger-dark"
                          className="is-clickable"
                          onClick={() =>
                            handleUnassignLicenseClick(
                              el.seats[0].assignedDevice!.id,
                              el.key
                            )
                          }
                          pl={2}
                        >
                          <FaTrash />
                        </Icon>
                      </Tooltip>
                    </CheckPermissions.Render>
                  </CheckPermissions>
                </Conditional.True>
                <Conditional.False>
                  <span className="is-italic">None</span>
                </Conditional.False>
              </Conditional>
            );
          },
          expandableContentRender: (el, parentElement) => {
            return (
              <Conditional condition={!!el.assignedDevice}>
                <Conditional.True>
                  <span>{el.assignedDevice?.serialNumber}</span>
                  <If condition={!!el.assignedDevice?.name?.length}>
                    <span> ({el.assignedDevice?.name})</span>
                  </If>
                  <CheckPermissions
                    permissions={
                      SoftwareSeatPermissions.SOFTWARE_SEATS_UNASSIGN
                    }
                  >
                    <CheckPermissions.Render>
                      <Tooltip message={UNASSIGN_TOOLTIP_MESSAGE}>
                        <Icon
                          color="danger-dark"
                          className="is-clickable"
                          onClick={() =>
                            handleUnassignLicenseClick(
                              el.assignedDevice!.id,
                              parentElement.key
                            )
                          }
                          pl={2}
                        >
                          <FaTrash />
                        </Icon>
                      </Tooltip>
                    </CheckPermissions.Render>
                  </CheckPermissions>
                </Conditional.True>
                <Conditional.False>
                  <span className="is-italic">None</span>
                </Conditional.False>
              </Conditional>
            );
          },
          title: 'Assigned device',
          sortable: false,
        },
        {
          key: 'assignedDevice.salesOrder.number',
          className: 'is-width-15',
          render: (el) => {
            if (isExpandableRow(el)) {
              return null;
            }
            const assignedDevice = el.seats[0]?.assignedDevice;
            return (
              <Conditional condition={!!assignedDevice}>
                <Conditional.True>
                  <If condition={!!assignedDevice?.salesOrder.id}>
                    <Link to={`/sales-orders/${assignedDevice?.salesOrder.id}`}>
                      {formatSalesOrderNumber(
                        assignedDevice?.salesOrder.number ?? ''
                      )}
                    </Link>
                  </If>
                </Conditional.True>
                <Conditional.False>
                  <span className="is-italic">None</span>
                </Conditional.False>
              </Conditional>
            );
          },
          expandableContentRender: (el) => {
            return (
              <Conditional condition={!!el.assignedDevice}>
                <Conditional.True>
                  <If condition={!!el.assignedDevice?.salesOrder.id}>
                    <Link
                      to={`/sales-orders/${el.assignedDevice?.salesOrder.id}`}
                    >
                      {formatSalesOrderNumber(
                        el.assignedDevice?.salesOrder.number ?? ''
                      )}
                    </Link>
                  </If>
                </Conditional.True>
                <Conditional.False>
                  <span className="is-italic">None</span>
                </Conditional.False>
              </Conditional>
            );
          },
          title: 'Assigned device SO#',
          sortable: false,
        },
        {
          key: 'softwareSeats.salesOrderLine.salesOrder.endUser',
          className: 'is-width-15',
          render: (el) => el.salesOrder.endUser ?? '',
          title: 'End user',
          sortable: true,
        },
      ],
      [
        handleUnassignLicenseClick,
        isExpandableRow,
        user,
        userHasSalesOrderManagePermissions,
        userIsJustViewOnlySalesOrderManager,
      ]
    );

  const expandableRow = useMemo(():
    | ExpandableRow<LicenseKeysTableRow, LicenseKeysExpandableTableRow>
    | undefined => {
    return {
      expandable: (row: LicenseKeysTableRow) => isExpandableRow(row),
      expandableRowIterator: (row: LicenseKeysTableRow) => {
        return row.seats;
      },
    };
  }, [isExpandableRow]);

  return (
    <>
      <PaginatedTable
        dataSource={dataSource}
        columns={columns}
        onChange={onChange}
        config={config}
        rowKey={'id'}
        empty={empty}
        form={form}
        isDisabledSelection={isDisabledRow}
        expandableRow={expandableRow}
      />
      <SoftwareUnassignmentConfirmationModal
        show={showConfirmationModal}
        licenseKey={selectedAssignmentData?.licenseKey ?? ''}
        onUnassign={handleConfirmUnassignmentClick}
        onCancel={handleCancelClick}
      />
    </>
  );
}

export default LicenseKeysTable;
